<template>
  <div id="dialog-claims-list">
    <span class="cls" @click="closeDialog"></span>
    <div class="paper">
      
      <!-- 헤더 -->
      <div class="header">
        <p>💰 전체 청구이력</p>
        <p @click="closeDialog"><i class="ph-bold ph-x"></i></p>
      </div>

      <div class="content">
        <!-- 요약 -->
        <div class="summary-bar">
          <div class="summary-item">
            <span class="label">총 고객수</span>
            <span class="value">{{ customerCount }}명</span>
          </div>
          <div class="summary-item">
            <span class="label">총 청구건수</span>
            <span class="value">{{ claimsList.length }}건</span>
          </div>
          <div class="summary-item highlight">
            <span class="label">총 청구금액</span>
            <span class="value">{{ formatMoney(totalClaimAmount) }}원</span>
          </div>
        </div>

        <!-- 필터/액션바 -->
        <div class="action-bar">
          <div class="filter-group">
            <select v-model="filterCustomer">
              <option value="">전체 고객</option>
              <option 
                v-for="cus in customersList" 
                :key="cus.idx" 
                :value="cus.idx"
              >
                {{ cus.name }}
              </option>
            </select>
            <select v-model="filterStatus">
              <option value="">전체 상태</option>
              <option value="0">접수</option>
              <option value="1">심사중</option>
              <option value="2">승인</option>
              <option value="3">지급완료</option>
              <option value="4">반려</option>
            </select>
          </div>
          <button class="add-btn" @click="openAddForm">
            <i class="ph-bold ph-plus"></i> 청구 추가
          </button>
        </div>

        <!-- 청구 리스트 -->
        <div class="claims-wrap" v-if="!loading">
          
          <div class="claims-list" v-if="filteredClaims.length > 0">
            <div 
              class="claim-card" 
              v-for="(claim, i) in filteredClaims" 
              :key="i"
            >
              <div class="claim-header">
                <div class="customer-badge">
                  <img :src="claim.customer_image || '/imgs/unknow_user.png'" alt="" />
                  <span>{{ claim.customer_name }}</span>
                </div>
                <span class="claim-status" :class="'status-' + claim.status">
                  {{ claimStatusText(claim.status) }}
                </span>
              </div>

              <div class="claim-info">
                <div class="company-info">
                  <span class="company-name">{{ getCompanyName(claim.insu_company_idx) }}</span>
                  <span class="claim-date">{{ claim.request_date }}</span>
                </div>
              </div>
              
              <div class="claim-body">
                <div class="claim-amount">{{ formatMoney(claim.amount) }}원</div>
                <div class="claim-content" v-if="claim.claim_content">{{ claim.claim_content }}</div>
              </div>
              
              <div class="claim-footer">
                <span class="confirm-date" v-if="claim.confirm_date">
                  확정: {{ claim.confirm_date }}
                </span>
                <span class="confirm-date" v-else></span>
                <div class="claim-actions">
                  <button class="btn-icon" @click="openEditForm(claim)" title="수정">
                    <i class="ph ph-pencil-simple"></i>
                  </button>
                  <button class="btn-icon danger" @click="deleteClaim(claim)" title="삭제">
                    <i class="ph ph-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="empty-state" v-else>
            <i class="ph ph-receipt"></i>
            <p>청구 이력이 없습니다</p>
            <button class="add-btn-empty" @click="openAddForm">
              <i class="ph-bold ph-plus"></i> 첫 청구 추가하기
            </button>
          </div>
        </div>

        <!-- 로딩 -->
        <div class="loading-state" v-else>
          <i class="ph ph-spinner"></i>
          <p>불러오는 중...</p>
        </div>
      </div>

      <!-- 추가/수정 폼 모달 -->
      <div class="form-modal" v-if="showForm" @click.self="closeForm">
        <div class="form-container">
          <div class="form-header">
            <h3>{{ isEditing ? '청구 수정' : '청구 추가' }}</h3>
            <button class="close-btn" @click="closeForm">
              <i class="ph-bold ph-x"></i>
            </button>
          </div>

          <div class="form-body">
            <div class="form-group">
              <label>고객 <span class="required">*</span></label>
              <select v-model="formData.customer_idx">
                <option value="">선택하세요</option>
                <option 
                  v-for="cus in customersList" 
                  :key="cus.idx" 
                  :value="cus.idx"
                >
                  {{ cus.name }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>보험회사 <span class="required">*</span></label>
              <select v-model="formData.insu_company_idx">
                <option value="">선택하세요</option>
                <option 
                  v-for="company in companyList" 
                  :key="company.idx" 
                  :value="company.idx"
                >
                  {{ company.name }}
                </option>
              </select>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>청구일 <span class="required">*</span></label>
                <input type="date" v-model="formData.request_date" />
              </div>
              <div class="form-group">
                <label>확정일</label>
                <input type="date" v-model="formData.confirm_date" />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>금액 (원) <span class="required">*</span></label>
                <input 
                  type="number" 
                  v-model="formData.amount" 
                  placeholder="0"
                />
              </div>
              <div class="form-group">
                <label>상태</label>
                <select v-model="formData.status">
                  <option value="0">접수</option>
                  <option value="1">심사중</option>
                  <option value="2">승인</option>
                  <option value="3">지급완료</option>
                  <option value="4">반려</option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label>청구 내용</label>
              <textarea 
                v-model="formData.claim_content" 
                placeholder="청구 사유나 내용을 입력하세요"
                rows="3"
              ></textarea>
            </div>
          </div>

          <div class="form-footer">
            <button class="btn-cancel" @click="closeForm">취소</button>
            <button class="btn-save" @click="saveClaim" :disabled="!isFormValid">
              {{ isEditing ? '수정' : '추가' }}
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, computed, inject, onMounted } from 'vue';

export default {
  name: 'ClaimsListDialog',
    props: {
      oth_showClaimList: {  // closeFunction 대신
        type: Function,
        required: true
      },
      reloadForSync: {
        type: Function,
        default: () => {}
      }
    },
  setup(props) {
    const sql_query = inject('sql_query');
    const member_info = inject('member_info');

    const loading = ref(true);
    const claimsList = ref([]);
    const customersList = ref([]);
    const companyList = ref([]);
    
    // 필터
    const filterCustomer = ref('');
    const filterStatus = ref('');

    // 폼 관련
    const showForm = ref(false);
    const isEditing = ref(false);
    const editingIdx = ref(null);
    const formData = ref({
      customer_idx: '',
      insu_company_idx: '',
      request_date: '',
      confirm_date: '',
      amount: '',
      status: '0',
      claim_content: ''
    });

    // 필터링된 청구 목록
    const filteredClaims = computed(() => {
      return claimsList.value.filter(claim => {
        if (filterCustomer.value && claim.customer_idx != filterCustomer.value) return false;
        if (filterStatus.value !== '' && claim.status != filterStatus.value) return false;
        return true;
      });
    });

    // 고객 수
    const customerCount = computed(() => {
      const unique = new Set(claimsList.value.map(c => c.customer_idx));
      return unique.size;
    });

    // 총 청구금액
    const totalClaimAmount = computed(() => {
      return filteredClaims.value.reduce((sum, claim) => sum + Number(claim.amount || 0), 0);
    });

    // 폼 유효성
    const isFormValid = computed(() => {
      return formData.value.customer_idx &&
             formData.value.insu_company_idx && 
             formData.value.request_date && 
             formData.value.amount;
    });

    // 고객 목록 조회
    async function fetchCustomers() {
      const sql = `SELECT idx, name, image FROM customers WHERE mb_id = '${member_info.mb_id}' ORDER BY name`;
      const result = await sql_query(sql);
      customersList.value = result;
    }

    // 보험회사 목록 조회
    async function fetchCompanyList() {
      const sql = `SELECT idx, name FROM insu_company ORDER BY sort_no, name`;
      const result = await sql_query(sql);
      companyList.value = result;
    }

    // 청구 목록 조회 (고객 정보 JOIN)
    async function fetchClaims() {
      loading.value = true;
      const sql = `
        SELECT 
          ic.*,
          c.name as customer_name,
          c.image as customer_image
        FROM insu_claims ic
        LEFT JOIN customers c ON ic.customer_idx = c.idx
        WHERE c.mb_id = '${member_info.mb_id}'
        ORDER BY ic.request_date DESC
      `;
      const result = await sql_query(sql);
      claimsList.value = result;
      loading.value = false;
    }

    // 보험회사명 가져오기
    function getCompanyName(idx) {
      const company = companyList.value.find(c => c.idx === idx);
      return company ? company.name : '-';
    }

    // 청구 상태 텍스트
    function claimStatusText(status) {
      const statusMap = {
        0: '접수',
        1: '심사중',
        2: '승인',
        3: '지급완료',
        4: '반려'
      };
      return statusMap[status] || '-';
    }

    // 금액 포맷
    function formatMoney(val) {
      if (!val) return '0';
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    // 추가 폼 열기
    function openAddForm() {
      isEditing.value = false;
      editingIdx.value = null;
      formData.value = {
        customer_idx: filterCustomer.value || '',
        insu_company_idx: '',
        request_date: new Date().toISOString().split('T')[0],
        confirm_date: '',
        amount: '',
        status: '0',
        claim_content: ''
      };
      showForm.value = true;
    }

    // 수정 폼 열기
    function openEditForm(claim) {
      isEditing.value = true;
      editingIdx.value = claim.idx;
      formData.value = {
        customer_idx: claim.customer_idx,
        insu_company_idx: claim.insu_company_idx,
        request_date: claim.request_date,
        confirm_date: claim.confirm_date || '',
        amount: claim.amount,
        status: String(claim.status),
        claim_content: claim.claim_content || ''
      };
      showForm.value = true;
    }

    // 폼 닫기
    function closeForm() {
      showForm.value = false;
      isEditing.value = false;
      editingIdx.value = null;
    }

    // 저장
    async function saveClaim() {
      if (!isFormValid.value) return;

      try {
        if (isEditing.value) {
          const sql = `
            UPDATE insu_claims SET
              customer_idx = '${formData.value.customer_idx}',
              insu_company_idx = '${formData.value.insu_company_idx}',
              request_date = '${formData.value.request_date}',
              confirm_date = ${formData.value.confirm_date ? `'${formData.value.confirm_date}'` : 'NULL'},
              amount = '${formData.value.amount}',
              status = '${formData.value.status}',
              claim_content = '${formData.value.claim_content.replace(/'/g, "''")}'
            WHERE idx = ${editingIdx.value}
          `;
          await sql_query(sql);
        } else {
          const sql = `
            INSERT INTO insu_claims SET
              customer_idx = '${formData.value.customer_idx}',
              insu_company_idx = '${formData.value.insu_company_idx}',
              request_date = '${formData.value.request_date}',
              confirm_date = ${formData.value.confirm_date ? `'${formData.value.confirm_date}'` : 'NULL'},
              amount = '${formData.value.amount}',
              status = '${formData.value.status}',
              claim_content = '${formData.value.claim_content.replace(/'/g, "''")}'
          `;
          await sql_query(sql);
        }

        closeForm();
        await fetchClaims();
        props.reloadForSync();
      } catch (error) {
        console.error('청구 저장 오류:', error);
        alert('저장 중 오류가 발생했습니다.');
      }
    }

    // 삭제
    async function deleteClaim(claim) {
      if (!confirm('이 청구 이력을 삭제하시겠습니까?')) return;

      try {
        const sql = `DELETE FROM insu_claims WHERE idx = ${claim.idx}`;
        await sql_query(sql);
        await fetchClaims();
        props.reloadForSync();
      } catch (error) {
        console.error('청구 삭제 오류:', error);
        alert('삭제 중 오류가 발생했습니다.');
      }
    }

    // 다이얼로그 닫기
    function closeDialog() {
      props.oth_showClaimList(false);
    }

    // 초기화
    onMounted(async () => {
      await fetchCustomers();
      await fetchCompanyList();
      await fetchClaims();
    });

    return {
      loading,
      claimsList,
      customersList,
      companyList,
      filteredClaims,
      customerCount,
      totalClaimAmount,
      filterCustomer,
      filterStatus,
      showForm,
      isEditing,
      formData,
      isFormValid,
      getCompanyName,
      claimStatusText,
      formatMoney,
      openAddForm,
      openEditForm,
      closeForm,
      saveClaim,
      deleteClaim,
      closeDialog,
    };
  },
}
</script>

<style scoped>
#dialog-claims-list {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  background: var(--color-surface);
  backdrop-filter: blur(var(--effect-blur));
}

.cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.paper {
  width: 100%;
  max-width: 720px;
  height: 100%;
  max-height: 90%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: var(--color-background);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
}

/* 헤더 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 60px;
  padding: 0 24px;
  box-sizing: border-box;
  background: var(--color-background);
  border-bottom: 1px solid var(--color-text-third);
}

.header > p {
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  font-size: var(--font-size-lg);
  cursor: pointer;
  margin: 0;
  padding: 0;
  font-weight: var(--font-weight-bold);
}

.header > p:last-child {
  font-size: var(--font-size-xl);
  font-weight: normal;
  opacity: 0.6;
}

/* 컨텐츠 */
.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 요약바 */
.summary-bar {
  display: flex;
  gap: 24px;
  padding: 16px 24px;
  background: var(--color-background-light);
  border-bottom: 1px solid var(--color-text-third);
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-item .label {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.summary-item .value {
  font-size: var(--font-size-lg);
  font-weight: 700;
  color: var(--color-text-primary);
}

.summary-item.highlight .value {
  color: var(--color-brand);
}

/* 액션바 */
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid var(--color-text-third);
  gap: 12px;
}

.filter-group {
  display: flex;
  gap: 8px;
}

.filter-group select {
  padding: 8px 12px;
  border: 1px solid var(--color-text-third);
  border-radius: 8px;
  font-size: var(--font-size-sm);
  background: var(--color-background);
  color: var(--color-text-primary);
  cursor: pointer;
}

.add-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--color-brand);
  color: var(--color-text-white);
  border: none;
  border-radius: 8px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.add-btn:hover {
  opacity: 0.9;
}

/* 청구 리스트 영역 */
.claims-wrap {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
}

.claims-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.claim-card {
  background: var(--color-background-light);
  border: 1px solid var(--color-text-third);
  border-radius: 12px;
  padding: 16px;
  transition: all 0.15s;
}

.claim-card:hover {
  border-color: var(--color-brand);
}

.claim-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.customer-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px;
  background: var(--color-background);
  border-radius: 20px;
}

.customer-badge img {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
}

.customer-badge span {
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-text-primary);
}

.claim-info {
  margin-bottom: 8px;
}

.company-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.company-name {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--color-text-primary);
}

.claim-date {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.claim-status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: #fff;
}

.claim-status.status-0 { background: #9e9e9e; }
.claim-status.status-1 { background: #ff9800; }
.claim-status.status-2 { background: #2196f3; }
.claim-status.status-3 { background: #4caf50; }
.claim-status.status-4 { background: #f44336; }

.claim-body {
  margin-bottom: 12px;
}

.claim-amount {
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 4px;
}

.claim-content {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.4;
}

.claim-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid var(--color-text-third);
}

.confirm-date {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.claim-actions {
  display: flex;
  gap: 8px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background);
  border: 1px solid var(--color-text-third);
  border-radius: 6px;
  cursor: pointer;
  color: var(--color-text-secondary);
  transition: all 0.15s;
}

.btn-icon:hover {
  border-color: var(--color-brand);
  color: var(--color-brand);
}

.btn-icon.danger:hover {
  border-color: var(--color-special-warning);
  color: var(--color-special-warning);
}

/* 빈 상태 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: var(--color-text-secondary);
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.empty-state p {
  font-size: var(--font-size-md);
  margin: 0 0 16px 0;
}

.add-btn-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--color-brand);
  color: var(--color-text-white);
  border: none;
  border-radius: 8px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
}

/* 로딩 */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  flex: 1;
  color: var(--color-text-secondary);
}

.loading-state i {
  font-size: 48px;
  margin-bottom: 12px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 폼 모달 */
.form-modal {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.form-container {
  width: 90%;
  max-width: 480px;
  background: var(--color-background);
  border-radius: 16px;
  overflow: hidden;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-text-third);
}

.form-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
  color: var(--color-text-primary);
}

.close-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: var(--color-background-light);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-size: 18px;
}

.form-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 60vh;
  overflow-y: auto;
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-row .form-group {
  flex: 1;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: var(--font-size-sm);
  font-weight: 500;
  color: var(--color-text-primary);
}

.form-group label .required {
  color: var(--color-special-warning);
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 10px 12px;
  border: 1px solid var(--color-text-third);
  border-radius: 8px;
  font-size: var(--font-size-md);
  background: var(--color-background);
  color: var(--color-text-primary);
  transition: border-color 0.15s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--color-brand);
}

.form-group textarea {
  resize: vertical;
  min-height: 80px;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid var(--color-text-third);
}

.btn-cancel,
.btn-save {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-cancel {
  background: var(--color-background-light);
  border: 1px solid var(--color-text-third);
  color: var(--color-text-primary);
}

.btn-save {
  background: var(--color-brand);
  border: none;
  color: var(--color-text-white);
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 스크롤바 */
.claims-wrap::-webkit-scrollbar {
  width: 6px;
}

.claims-wrap::-webkit-scrollbar-track {
  background: transparent;
}

.claims-wrap::-webkit-scrollbar-thumb {
  background: var(--color-text-third);
  border-radius: 3px;
}

/* 모바일 */
@media (max-width: 768px) {
  .paper {
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
  }

  .summary-bar {
    flex-wrap: wrap;
    gap: 16px;
  }

  .action-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    width: 100%;
  }

  .filter-group select {
    flex: 1;
  }

  .add-btn {
    justify-content: center;
  }

  .form-row {
    flex-direction: column;
  }
}
</style>